"use client";

import { useSyncExternalStore } from "react";
import Link from "next/link";
import Image from "next/image";
import { FormattedMessage } from "react-intl";
import type { PostSummary } from "@/lib/ressources";
import { categoryToSlug } from "@/lib/category-slugs";
import { CATEGORY_META } from "./category-meta";
import { DraftBadge } from "./DraftBadge";
import { isRecentPost } from "./recent";

// Pulsing dot + label marking a recently published article, overlaid on the
// cover image like a notification badge. The "new" state is computed after
// mount (client clock) to keep it accurate without a rebuild and to avoid an
// SSR/CSR hydration mismatch on the date.
function NewBadge() {
  return (
    <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-green-ink shadow-sm backdrop-blur-sm">
      <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-main opacity-75 motion-reduce:hidden" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-main" />
      </span>
      <FormattedMessage id="ressources.new.label" />
    </span>
  );
}

// Nothing to subscribe to: the badge just needs to read `false` on the server
// and the real, clock-based value once we're in the browser.
const subscribeToNothing = () => () => {};
const notNewOnServer = () => false;

function formatDate(date: string, locale: "fr" | "en" = "fr"): string {
  return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "fr-FR", { dateStyle: "long" }).format(
    new Date(date),
  );
}

/**
 * An article card: cover, category, title, excerpt, date and reading time, the whole card
 * being one link.
 *
 * @param post - The article.
 * @param locale - Page language (link target and date format).
 * @param headingLevel - Level of the title in the page outline: `h2` when the cards sit
 *   right under the page `h1` (category pages), `h3` under a section `h2` (default).
 * @param priority - Load the cover eagerly, for the first card above the fold (LCP).
 */
export function PostCard({
  post,
  locale = "fr",
  headingLevel = "h3",
  priority = false,
}: {
  post: PostSummary;
  locale?: "fr" | "en";
  headingLevel?: "h2" | "h3";
  priority?: boolean;
}) {
  const meta = CATEGORY_META[post.category];
  const Heading = headingLevel;
  const isNew = useSyncExternalStore(
    subscribeToNothing,
    () => isRecentPost(post.date),
    notNewOnServer,
  );

  const href = `/resources/${categoryToSlug(post.category)}/${post.slug}${locale === "en" ? "/en" : ""}`;

  return (
    // The link stays put and the card inside it lifts: a lifting link would slide out from
    // under a pointer resting on its lower edge and jitter.
    <Link href={href} className="group block h-full rounded-xl">
      <div className="flex h-full flex-col overflow-hidden rounded-xl bg-slate-100 transition-transform duration-300 ease-in-out group-hover:-translate-y-1">
      <span className="relative block aspect-video w-full bg-gray-ulight">
        {/* Every grid rendering this card is 1 / 2 / 3 columns (sm / lg), in containers up to 1440 px.
            Decorative: the title right below names the card (an alt would read it twice). */}
        <Image
          src={post.cover}
          alt=""
          fill
          priority={priority}
          sizes="(min-width: 1536px) 460px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="object-contain"
        />
        {isNew && <NewBadge />}
        {/* Left corner so it never collides with the "new" badge on the right. */}
        <DraftBadge draft={post.draft} className="absolute left-3 top-3" />
      </span>
      <div className="flex flex-col gap-2 p-5">
        <div className="flex items-center">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${meta.color} ${meta.bg}`}
          >
            <FormattedMessage id={meta.labelKey} />
          </span>
        </div>
        <Heading className="text-lg font-bold leading-snug text-text-main transition-colors group-hover:text-brand 2xl:text-xl">
          {post.title}
        </Heading>
        <p className="line-clamp-3 text-sm text-gray-main 2xl:text-base">{post.excerpt}</p>
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-main 2xl:text-sm">
          <time dateTime={post.date}>{formatDate(post.date, locale)}</time>
          <span aria-hidden="true">·</span>
          <span>
            <FormattedMessage id="ressources.readingTime" values={{ minutes: post.readingTimeMinutes }} />
          </span>
        </div>
      </div>
      </div>
    </Link>
  );
}
